import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useAdminAreaById,
  useFetchAdminAreasByIds,
  useGetAdminAreaHierarchy
} from "@/api/adminArea/userAdminAreaAPI";
import { useCountryQuery } from "@/api/country/useCountryAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import AdminAreaSelection from "@/components/adminAreaSelection";
import { isGlobalFilterAdminAreaSelected } from "@/components/dataset/datasetExplorer/poi/utils";
import LimitWarning from "@/components/ui/LimitWarning";
import LoadingWrapper from "@/components/ui/LoadingWrapper";
import { MESSAGE } from "@/constants/message";
import {
  createAdminAreaTreeFilter,
  generateAttributeFilterOptions,
  getValueOfHighestLevelAdminArea,
  handleAdminAreaChangeHelper
} from "@/helpers/datasetAttributeHelper";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { useSpatialInputRowsLimit } from "@/hooks/spatialAnalysis/useSpatialInputRowsLimit";
import { useConfirmationModal } from "@/hooks/ui/modal/useConfirmationModal";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import {
  toggleUseGlobalFilter,
  updateFilters
} from "@/store/slice/datasetSlice";
import {
  applyAdminTreeGlobalFilter,
  setAdminAreaSettings as setGlobalFilterAdminAreaSettings,
  setAdminAreaSettingsData as setGlobalFilterAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp as setGlobalFilterAdminAreaSettingsDataTemp,
  setSelectedAreaIdsTemp
} from "@/store/slice/globalFilterSlice";
import { setLegendFilters } from "@/store/slice/mapSlice";
import {
  setAdminAreaAddress,
  setAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp,
  setAdminTreeSettingsSelectedIds,
  setAdminTreeSettingsTempSelectedIds,
  setInputRowCount,
  setOverrideGlobalFilter,
  setSpatialConfig,
  updateCatchmentDatasetData
} from "@/store/slice/spatialSlice";
import { AdminAreaData, AdministrativeArea } from "@/types/adminArea";
import type { AttributeOption, Dataset, DatasetFilter } from "@/types/dataset";
import { type AdminAreaSettings, AreaInputType } from "@/types/spatial";

import { area } from "@turf/turf";
import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

const TEST_ID_PREFIX = "admin-area-settings-popup";

interface Props {
  onClose: () => void;
  type?: Extract<keyof RootState, "spatial" | "globalFilter">;
}
interface ConfigType {
  setSettings: (settings: AdminAreaSettings | null) => void;
  dataTemp: AdminAreaData | null;
  setData: (data: AdminAreaData | null, areaSize: number) => void;
  afterTreeAction: (areaSize: number) => void;
}

// Get Data Set Linked Dataset and Unlinked Dataset and all dataset ids
const getDatasets = (datasets: Record<string, Dataset>) => {
  const all = Object.values(datasets);
  return {
    allDatasetIds: all.map((d) => d.data.id),
    linkedDatasetIds: all
      .filter((d) => d.is_global_admin_area)
      .map((d) => d.data.id),
    unLinkedDatasetIds: all
      .filter((d) => !d.is_global_admin_area)
      .map((d) => d.data.id)
  };
};

const AdminAreaSettingsPopup = ({ onClose, type = "spatial" }: Props) => {
  const dispatch = useDispatch();
  const { isAdminTreeEnabled, isGlobalFilterOverrideEnabled } =
    useAppFeatures();
  const { clearLoadingState, showLoading } = useLoadingAndToast();
  const { showConfirmation } = useConfirmationModal();
  const { datasets, filters } = useSelector(
    (state: RootState) => state.dataset
  );
  const {
    adminAreaSettings,
    adminAreaSettingsDataTemp,
    spatialConfig,
    adminTreeSettingsTemp,
    overrideGlobalFilter,
    adminTreeSettings,
    catchmentSettings
  } = useSelector((state: RootState) => state.spatial);
  const {
    selectedAreaIdsTemp,
    selectedAreaIds: globalSelectedAreaIds,
    adminAreaSettings: globalAdminAreaSettings,
    adminAreaSettingsDataTemp: globalAdminAreaSettingsDataTemp
  } = useSelector((state: RootState) => state.globalFilter);
  const { legendFilters } = useSelector((state: RootState) => state.map);
  const isGlobal = type === "globalFilter";

  const [localAdmAreaSetting, setLocalAdmAreaSetting] =
    useState<AdminAreaSettings | null>(
      isGlobal ? globalAdminAreaSettings : adminAreaSettings
    );

  const selectedIds = useMemo(
    () =>
      isGlobal
        ? selectedAreaIdsTemp
        : (adminTreeSettingsTemp?.selectedAreaIds ?? []),
    [isGlobal, selectedAreaIdsTemp, adminTreeSettingsTemp?.selectedAreaIds]
  );

  const adminAreaSettingsData = isGlobal
    ? globalAdminAreaSettingsDataTemp
    : adminAreaSettingsDataTemp;

  const [firstLoad, setFirstLoad] = useState(true);

  const { fitMapToLayerBound } = useMap();
  const [adminAreaId, setAdminAreaId] = useState<string | null>(
    getValueOfHighestLevelAdminArea(localAdmAreaSetting)
  );

  const { data: adminAreaDataById = null } = useAdminAreaById(
    adminAreaId,
    undefined,
    {
      enabled: !firstLoad && !!adminAreaId
    }
  );

  const { data: lv0Data } = useCountryQuery(localAdmAreaSetting?.lv0);
  const fetchAdminAreaByIds = useFetchAdminAreasByIds();

  const handleSetSelectedIds = useCallback(
    (ids: string[]) => {
      if (isGlobal) {
        dispatch(setSelectedAreaIdsTemp(ids));
      } else {
        dispatch(setAdminTreeSettingsTempSelectedIds(ids));
      }
    },
    [isGlobal, dispatch]
  );

  const handleAdminAreaDataChange = useCallback(
    (data: AdminAreaData | null) => {
      if (isGlobal) {
        dispatch(setGlobalFilterAdminAreaSettingsDataTemp(data));
      } else {
        dispatch(setAdminAreaSettingsDataTemp(data));
      }
    },
    [isGlobal, dispatch]
  );

  const adminTreeConfig = {
    enabled: isAdminTreeEnabled,
    selectedIds,
    setSelectedIds: async (ids: string[]) => {
      handleSetSelectedIds(ids);

      if (ids.length === 0 && lv0Data) {
        handleAdminAreaDataChange(lv0Data);
        fitMapToLayerBound(lv0Data);
        return;
      }

      const results = await fetchAdminAreaByIds(ids);

      const newAdminTreeData = results.reduce(
        (acc: Record<string, AdministrativeArea>, result) => {
          const adminArea = result;
          if (adminArea) {
            acc[adminArea.id] = adminArea;
          }
          return acc;
        },
        {}
      );

      const allData = ids.map((id) => newAdminTreeData[id]).filter(Boolean);
      fitMapToLayerBound({ data: allData });
      handleAdminAreaDataChange({ data: allData });
    }
  };

  const handleAdminAreaChange = (admAreaSetting: AdminAreaSettings | null) => {
    setLocalAdmAreaSetting(admAreaSetting);
    if (isAdminTreeEnabled) {
      handleSetSelectedIds([]); // reset selected ids on country change
    }

    const adminAreaIdHighest = getValueOfHighestLevelAdminArea(admAreaSetting);
    if (!adminAreaIdHighest) return;
    setAdminAreaId(adminAreaIdHighest);
  };

  const resetResolution = (areaSize: number) => {
    dispatch(
      setSpatialConfig({
        ...spatialConfig,
        areaSize,
        gridResolution: null,
        h3Resolution: null
      })
    );
  };

  const handleTreeAdminAreaInput = () => {
    dispatch(setAdminTreeSettingsSelectedIds(selectedIds));
    dispatch(setAdminAreaSettingsData(adminAreaSettingsData));
    dispatch(setAdminTreeSettingsTempSelectedIds([]));
  };

  const { data: userProfile } = useGetUserProfileAPI();
  const { handleUpdateDatasetData, generateDatasetData } = useDataset();

  const { data: adminAreaHierarchyData } = useGetAdminAreaHierarchy(
    localAdmAreaSetting?.lv0?.value ?? "",
    {
      enabled: isAdminTreeEnabled && !!localAdmAreaSetting?.lv0?.value
    }
  );

  // Build new filter for tree mode
  const buildTreeFilter = (
    savedFilters: DatasetFilter[],
    filterOptions: AttributeOption[]
  ): DatasetFilter[] => {
    const otherFilters = savedFilters.filter(
      (f) => !f.attributeCode?.includes("adm_area_level")
    );

    const adminTreeFilter = createAdminAreaTreeFilter(
      selectedIds,
      adminAreaHierarchyData?.data ?? [],
      filterOptions
    );

    return adminTreeFilter ? [adminTreeFilter, ...otherFilters] : otherFilters;
  };

  const hasAdminSelection =
    !!localAdmAreaSetting?.lv1 || selectedIds.length > 0;

  // Has the user changed the area from the one already saved to the global
  // filter? On open it matches (not dirty); picking a different area makes it
  // dirty. Drives the H3 fallback below (use stored count only while unchanged).
  const isAreaDirty = useMemo(() => {
    if (isAdminTreeEnabled) {
      const saved = new Set(globalSelectedAreaIds ?? []);
      return (
        selectedIds.length !== saved.size ||
        selectedIds.some((id) => !saved.has(id))
      );
    }
    return (
      getValueOfHighestLevelAdminArea(localAdmAreaSetting) !==
      getValueOfHighestLevelAdminArea(globalAdminAreaSettings)
    );
  }, [
    isAdminTreeEnabled,
    selectedIds,
    globalSelectedAreaIds,
    localAdmAreaSetting,
    globalAdminAreaSettings
  ]);

  const { linkedDatasetIds, allDatasetIds } = useMemo(
    () => getDatasets(datasets),
    [datasets]
  );

  // The global admin area only re-filters linked datasets, so measure only
  // those via `/stats` with the candidate region applied. While the area is
  // unchanged, fall back to the stored count (H3 can't be re-measured pre-save).
  const inputRowsStatsTargets = useMemo(() => {
    if (!isGlobal) return [];
    return linkedDatasetIds.map((instanceId) => {
      const dataset = datasets[instanceId];
      const datasetSavedFilters = filters[instanceId] || [];
      const filterOptions = generateAttributeFilterOptions(dataset.data);
      const newFilter = isAdminTreeEnabled
        ? buildTreeFilter(datasetSavedFilters, filterOptions)
        : handleAdminAreaChangeHelper(
            localAdmAreaSetting,
            filterOptions,
            datasetSavedFilters
          );
      return {
        datasetId: instanceId.split("_")[0],
        filters: newFilter,
        h3Configuration: dataset.h3Configuration,
        group_by: dataset?.group_by,
        fallbackDatasetId: isAreaDirty ? undefined : instanceId
      };
    });
    // buildTreeFilter / helpers are stable closures over the listed deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGlobal,
    linkedDatasetIds,
    datasets,
    filters,
    isAdminTreeEnabled,
    localAdmAreaSetting,
    selectedIds,
    adminAreaHierarchyData,
    isAreaDirty
  ]);

  const isEnabledQueriesStatusCount =
    isGlobal && hasAdminSelection && isAreaDirty && isAdminTreeEnabled;
  // Excluding ALL datasets zeroes the base, so the total counts only the linked
  // targets — unlinked datasets aren't counted here (full analysis total is
  // enforced in the spatial-analysis popup). Probe only once an area is picked.
  const {
    isOverLimit: isInputRowsOverLimit,
    limit: inputRowsLimit,
    isFetchingStatusDataset
  } = useSpatialInputRowsLimit({
    statsTargets: inputRowsStatsTargets,
    excludeDatasetIds: allDatasetIds,
    enabled: isEnabledQueriesStatusCount
  });

  // Update Linked Datasets
  const updateLinkedDatasets = async ({
    settings,
    linkedDatasetIds,
    isTree,
    isConfirm = false
  }: {
    settings: AdminAreaSettings | null;
    linkedDatasetIds: string[];
    isTree: boolean;
    isConfirm?: boolean;
  }) => {
    /** Catchment-dataset registry lives on the spatial slice rather than the
     * main `dataset` slice. Mirror the main-dataset re-fetch loop below for
     * every catchment dataset that opted into the global admin area filter,
     * so the catchment data stays in sync with the new admin area selection.
     */
    const linkedCatchmentEntries = Object.entries(
      catchmentSettings?.datasetConfig?.datasets ?? {}
    ).filter(([, entry]) => entry.dataset?.is_global_admin_area);

    if (!linkedDatasetIds.length && !linkedCatchmentEntries.length) return;
    showLoading("Updating datasets...", true);
    try {
      const mainDatasetTasks = linkedDatasetIds.map(async (datasetId) => {
        const dataset = datasets[datasetId];
        if (!dataset) return;

        const savedFilters = filters[datasetId] || [];
        const filterOptions = generateAttributeFilterOptions(dataset.data);

        const newFilter = isTree
          ? buildTreeFilter(savedFilters, filterOptions)
          : handleAdminAreaChangeHelper(settings, filterOptions, savedFilters);

        const { rawDataset } = await fetchDatasetStream({
          datasetId: datasetId.split("_")[0],
          filters: newFilter,
          h3Configuration: dataset.h3Configuration,
          timezone: userProfile?.timezone,
          ...(dataset.group_by && dataset.group_by !== "none"
            ? { group_by: dataset.group_by }
            : {}),

          generateDatasetData
        });

        if (rawDataset) {
          handleUpdateDatasetData(
            datasetId,
            rawDataset,
            dataset.h3Configuration
          );
          // Re-accumulate: the region filter changed this dataset's row count.
          if (isAdminTreeEnabled) {
            dispatch(
              setInputRowCount({
                datasetId,
                count: rawDataset.rows?.length ?? 0
              })
            );
          }
          // Clear stale legend filters: the admin area refresh re-computes
          // colorRanges with new min/max boundaries. Old {min,max} entries in
          // legendFilters would no longer match any displayed legend item,
          // causing permanently hidden features on the new layer.
          dispatch(setLegendFilters({ ...legendFilters, [datasetId]: [] }));
        }
        if (isConfirm) {
          const { unLinkedDatasetIds } = getDatasets(datasets);
          if (unLinkedDatasetIds.length) {
            unLinkedDatasetIds.forEach((datasetId) =>
              dispatch(
                toggleUseGlobalFilter({ datasetId, useGlobalFilter: true })
              )
            );
          }
        }
        dispatch(updateFilters({ datasetId, filters: newFilter }));
      });

      const catchmentDatasetTasks = linkedCatchmentEntries.map(
        async ([uniqueId, entry]) => {
          const sourceDatasetId = entry.rawDataset?.dataset_id ?? uniqueId;
          const filterOptions = generateAttributeFilterOptions(
            entry.dataset?.data
          );
          const newFilter = isTree
            ? buildTreeFilter(entry.filters ?? [], filterOptions)
            : handleAdminAreaChangeHelper(
                settings,
                filterOptions,
                entry.filters ?? []
              );

          const { rawDataset } = await fetchDatasetStream({
            datasetId: sourceDatasetId.split("_")[0],
            filters: newFilter,
            h3Configuration: entry.dataset?.h3Configuration,
            timezone: userProfile?.timezone,
            ...(entry.dataset.group_by && entry.dataset.group_by !== "none"
              ? { group_by: entry.dataset.group_by }
              : {}),
            generateDatasetData
          });

          dispatch(
            updateCatchmentDatasetData({
              datasetId: uniqueId,
              rawDataset: rawDataset ?? undefined,
              filters: newFilter
            })
          );
        }
      );

      await Promise.all([...mainDatasetTasks, ...catchmentDatasetTasks]);
    } finally {
      clearLoadingState();
    }
  };

  // Calculate total area size from admin area data
  const calculateAreaSize = (data: AdminAreaData | null) =>
    data?.data?.reduce((total, item) => total + area(item.boundary), 0) || 0;

  // Create Config Saving
  const createConfig = ({
    isGlobal,
    isTree,
    isConfirm = false,
    linkedDatasetIds,
    needApplyToDatasetsFilter,
    needApplyToAreaInput,
    shouldResetSpatialAdminAreaOverride,
    adminAreaSettingsData
  }: {
    isGlobal: boolean;
    isTree: boolean;
    isConfirm?: boolean;
    linkedDatasetIds: string[];
    needApplyToDatasetsFilter: boolean;
    needApplyToAreaInput: boolean;
    shouldResetSpatialAdminAreaOverride: boolean;
    adminAreaSettingsData: AdminAreaData | null;
  }) => {
    if (!isGlobal) {
      return {
        setSettings: (settings: AdminAreaSettings | null) => {
          dispatch(setAdminAreaAddress(settings));
        },
        dataTemp: adminAreaSettingsData,
        setData: (data: AdminAreaData | null, areaSize: number) => {
          dispatch(setAdminAreaSettingsData(data));
          resetResolution(areaSize);
        },
        afterTreeAction: (areaSize: number) => {
          handleTreeAdminAreaInput();
          resetResolution(areaSize);
        }
      };
    }

    return {
      setSettings: async (settings: AdminAreaSettings | null) => {
        dispatch(setGlobalFilterAdminAreaSettings(settings));

        if (needApplyToAreaInput) {
          dispatch(setAdminAreaAddress(settings));
        }

        if (shouldResetSpatialAdminAreaOverride) {
          dispatch(setOverrideGlobalFilter(false));
          dispatch(setAdminAreaAddress(settings));
        }

        if (needApplyToDatasetsFilter) {
          await updateLinkedDatasets({
            settings,
            linkedDatasetIds,
            isTree,
            isConfirm
          });
        }
      },
      dataTemp: adminAreaSettingsData,
      setData: (data: AdminAreaData | null, areaSize: number) => {
        dispatch(setGlobalFilterAdminAreaSettingsData(data));

        // Apply set admin area settings data from global filter data to area input when user selected administrative area first on area input.
        if (needApplyToAreaInput || shouldResetSpatialAdminAreaOverride) {
          dispatch(setAdminAreaSettingsData(data));
          resetResolution(areaSize);
        }
      },
      afterTreeAction: (areaSize: number) => {
        dispatch(applyAdminTreeGlobalFilter());

        if (needApplyToAreaInput || shouldResetSpatialAdminAreaOverride) {
          if (shouldResetSpatialAdminAreaOverride)
            dispatch(setOverrideGlobalFilter(false));
          handleTreeAdminAreaInput();
          resetResolution(areaSize);
        }

        if (shouldResetSpatialAdminAreaOverride) {
          dispatch(setAdminTreeSettingsSelectedIds(selectedIds));
        }
      }
    };
  };

  // Apply Config Setting
  const applyConfigSetting = ({
    isTree = false,
    config,
    adminAreaSettings
  }: {
    isTree: boolean;
    config: ConfigType;
    adminAreaSettings: AdminAreaSettings | null;
  }) => {
    config.setSettings(adminAreaSettings);

    if (!config.dataTemp?.data?.length) {
      return {
        isTree,
        config,
        adminAreaSettings
      };
    }

    const areaSize = calculateAreaSize(config.dataTemp);

    if (isTree) {
      config.afterTreeAction(areaSize);
    } else {
      config.setData(config.dataTemp, areaSize);
    }

    return {
      isTree,
      config,
      adminAreaSettings
    };
  };

  // Handle Save Setting
  const onSaveSetting = () => {
    const isTree = isAdminTreeEnabled;

    const { linkedDatasetIds, allDatasetIds } = getDatasets(datasets);

    const isGlobalFilterSelected = isGlobalFilterAdminAreaSelected({
      isAdminTreeEnabled: isTree,
      isGlobalFilterOverrideEnabled,
      globalSelectedAreaIds: globalSelectedAreaIds, // global admin area selected multiple area
      globalAdminArea: globalAdminAreaSettings // global admin area selected single area
    });

    const needApplyToAreaInput =
      !overrideGlobalFilter &&
      spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA;

    const shouldResetSpatialAdminAreaOverride =
      adminTreeSettings.selectedAreaIds.length === 0 &&
      !adminAreaSettings?.lv1 &&
      spatialConfig.areaInput?.value === AreaInputType.ADMIN_AREA &&
      overrideGlobalFilter;

    const shouldShowModal =
      isGlobal && isGlobalFilterSelected && allDatasetIds.length > 0;

    const baseConfig = {
      isGlobal,
      isTree,
      needApplyToAreaInput,
      shouldResetSpatialAdminAreaOverride,
      adminAreaSettingsData
    };

    const saveConfig = ({
      datasetIds,
      isConfirm = false
    }: {
      datasetIds: string[];
      isConfirm?: boolean;
    }) => {
      const config = createConfig({
        ...baseConfig,
        isConfirm,
        linkedDatasetIds: datasetIds,
        needApplyToDatasetsFilter: datasetIds.length > 0
      });
      applyConfigSetting({
        isTree,
        config,
        adminAreaSettings: localAdmAreaSetting
      });
      onClose();
    };

    if (!shouldShowModal) {
      saveConfig({ datasetIds: linkedDatasetIds });
      return;
    }

    // Show confirmation modal when global filter is selected and linked datasets exist; otherwise save directly.
    // Note: Labels are intentionally reversed — onConfirm updates linked datasets, onCancel applies to all global admin areas.
    showConfirmation({
      title: "Confirmation",
      message: MESSAGE.DATASET_CONFIRMATION_UPDATE_FROM_GLOBAL_FILTER,
      confirmLabel: MESSAGE.DATASET_CONFIRMATION_KEEP_LABEL_FROM_GLOBAL_FILTER,
      cancelLabel: MESSAGE.DATASET_CONFIRMATION_APPLY_LABEL_FROM_GLOBAL_FILTER,
      onConfirm: () => saveConfig({ datasetIds: linkedDatasetIds }),
      onCancel: () => saveConfig({ datasetIds: allDatasetIds, isConfirm: true })
    });
  };

  const handleSetDataPolygon = useCallback(() => {
    // Case user not select any country
    if (!localAdmAreaSetting?.lv0) {
      handleAdminAreaDataChange(null);
      return;
    }

    if (isAdminTreeEnabled) {
      if (selectedIds.length === 0) {
        if (lv0Data) {
          handleAdminAreaDataChange(lv0Data);
          fitMapToLayerBound(lv0Data);
        }
      } else if (adminAreaSettingsData) {
        fitMapToLayerBound(adminAreaSettingsData);
      }
      return;
    }

    // Case user select country but not select any admin area
    if (!localAdmAreaSetting?.lv1) {
      handleAdminAreaDataChange(lv0Data ?? null);
      if (lv0Data) {
        fitMapToLayerBound(lv0Data);
      }
      return;
    }
    // Case cover admin area from level 2 to level 5
    handleAdminAreaDataChange(adminAreaDataById ?? null);
    if (adminAreaDataById) {
      fitMapToLayerBound(adminAreaDataById);
    }
  }, [
    localAdmAreaSetting,
    isAdminTreeEnabled,
    lv0Data,
    adminAreaDataById,
    selectedIds,
    adminAreaSettingsData,
    fitMapToLayerBound,
    handleAdminAreaDataChange
  ]);

  useEffect(() => {
    // Check if it's the first load, will ignore
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }
    handleSetDataPolygon();
  }, [handleSetDataPolygon, firstLoad]);

  return (
    <div
      id="admin-area-settings"
      className={twMerge(
        "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
        "flex flex-col p-[16px] absolute left-[370px] top-[30px]",
        "max-h-[calc(100vh-54px)]"
      )}
    >
      <div className="flex flex-row justify-between items-start">
        <div className="flex flex-row items-center gap-2">
          <h2 className="text-txtHeading font-bold text-[20px]">
            Administrative Area
          </h2>
        </div>
        <button
          className="bg-transparent border-0 p-0"
          onClick={onClose}
          data-testid="btn-close-adminArea"
          aria-label="Close"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      <div
        className={twMerge(
          "flex flex-col mt-2",
          isAdminTreeEnabled &&
            "flex-1 min-h-0 overflow-y-auto custom-scrollbar"
        )}
      >
        <p className="text-txtBody text-[10px]">
          Countries have varying levels of administrative areas.
        </p>
        <LoadingWrapper isLoading={isFetchingStatusDataset} overlay>
          <AdminAreaSelection
            selectedData={localAdmAreaSetting}
            onSelectData={handleAdminAreaChange}
            treeConfig={adminTreeConfig}
            limitWarningSlot={
              isGlobal &&
              hasAdminSelection &&
              isInputRowsOverLimit &&
              !isFetchingStatusDataset ? (
                <LimitWarning
                  className="my-3"
                  title={MESSAGE.LIMIT_THRESHOLD_WARNING.TITLE}
                  message={MESSAGE.LIMIT_THRESHOLD_WARNING.MESSAGE(
                    inputRowsLimit,
                    "filter-area"
                  )}
                />
              ) : null
            }
            options={{
              showCountryLevel: true,
              showLabelAboveInput: true,
              showLabel: false,
              prefixLabel: "Search",
              required: {
                lv0: true,
                lv1: false,
                lv2: false,
                lv3: false,
                lv4: false
              }
            }}
          />
        </LoadingWrapper>
      </div>
      <Button
        className={twMerge(
          "h-[40px] mt-3 border bg-teal-500 text-white text-sm font-bold flex-none",
          "disabled:border-none disabled:bg-gainsboro disabled:text-white"
        )}
        disabled={
          (!localAdmAreaSetting?.lv1 && selectedIds.length === 0) ||
          // Block Save (global) when linked datasets exceed the limit, or hold
          // while `/stats` is still counting.
          (isGlobal &&
            hasAdminSelection &&
            (isInputRowsOverLimit || isFetchingStatusDataset))
        }
        onClick={onSaveSetting}
        data-testid={`${TEST_ID_PREFIX}-save-button`}
      >
        <span>Save</span>
      </Button>
    </div>
  );
};

export default AdminAreaSettingsPopup;
